"use client";

import { useState } from "react";
import { skills, softSkills, methodologies, getCategories, Category } from "#/data/skills";
import * as m from "motion/react-m";
import { MagicCard } from "#/components/magicui/magic-card";
import { Card, CardContent, CardHeader, CardTitle } from "#/components/ui/card";
import { Badge } from "#/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "#/components/ui/tabs";
import { FaFilter } from "react-icons/fa";
import dynamic from "next/dynamic";

const Section = dynamic(() => import("./section"), {
  ssr: false,
});

const getYearColor = (years: number) => {
  if (years >= 10) {
    return {
      bg: "bg-indigo-100 dark:bg-indigo-950",
      text: "text-indigo-800 dark:text-indigo-300",
      border: "border-indigo-300 dark:border-indigo-700",
      badge: "bg-indigo-200 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200",
    };
  } else if (years >= 7) {
    return {
      bg: "bg-blue-100 dark:bg-blue-950",
      text: "text-blue-800 dark:text-blue-300",
      border: "border-blue-300 dark:border-blue-700",
      badge: "bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200",
    };
  } else if (years >= 4) {
    return {
      bg: "bg-teal-100 dark:bg-teal-950",
      text: "text-teal-800 dark:text-teal-300",
      border: "border-teal-300 dark:border-teal-700",
      badge: "bg-teal-200 dark:bg-teal-800 text-teal-800 dark:text-teal-200",
    };
  } else {
    return {
      bg: "bg-emerald-100 dark:bg-emerald-950",
      text: "text-emerald-800 dark:text-emerald-300",
      border: "border-emerald-300 dark:border-emerald-700",
      badge: "bg-emerald-200 dark:bg-emerald-800 text-emerald-800 dark:text-emerald-200",
    };
  }
};

const categoryNames: Record<string, string> = {
  code: "Langages",
  framework: "Technologies",
  tool: "Outils",
  design: "Design",
  database: "Base de données",
  devops: "DevOps",
  ia: "Intelligence Artificielle",
  other: "Autre",
};

const getCategoryColor = (category: Category) => {
  switch (category) {
    case "code":
      return "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900 dark:text-blue-100 dark:border-blue-700";
    case "framework":
      return "bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900 dark:text-purple-100 dark:border-purple-700";
    case "design":
      return "bg-pink-100 text-pink-800 border-pink-300 dark:bg-pink-900 dark:text-pink-100 dark:border-pink-700";
    case "tool":
      return "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900 dark:text-amber-100 dark:border-amber-700";
    case "database":
      return "bg-green-100 text-green-800 border-green-300 dark:bg-green-900 dark:text-green-100 dark:border-green-700";
    case "devops":
      return "bg-red-100 text-red-800 border-red-300 dark:bg-red-900 dark:text-red-100 dark:border-red-700";
    case "ia":
      return "bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-900 dark:text-indigo-100 dark:border-indigo-700";
    case "other":
      return "bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600";
    default:
      return "bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600";
  }
};

export const SkillsSection = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const categories = getCategories();

  const sortedSkills = [...skills].sort((a, b) => b.years - a.years);
  const filteredSkills = selectedCategory
    ? sortedSkills.filter((skill) => skill.category === selectedCategory)
    : sortedSkills;

  const skillsByYears: Record<number, typeof skills> = {};

  filteredSkills.forEach((skill) => {
    if (!skillsByYears[skill.years]) {
      skillsByYears[skill.years] = [];
    }
    skillsByYears[skill.years].push(skill);
  });

  return (
    <Section id="skills" title="Mes compétences">
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
        <div className="lg:col-span-4">
          <div className="mb-6">
            <div className="mb-2 flex items-center">
              <FaFilter className="mr-2 text-gray-500" />
              <h3 className="text-sm font-medium">Filtrer par catégorie:</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`rounded-full border px-3 py-1.5 text-xs transition-all ${
                  selectedCategory === null
                    ? "border-gray-800 bg-gray-800 text-white dark:border-white dark:bg-white dark:text-gray-800"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                }`}
              >
                Tous
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`rounded-full border px-3 py-1.5 text-xs transition-all ${
                    selectedCategory === category
                      ? `${getCategoryColor(category)} font-medium`
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                  }`}
                >
                  {categoryNames[category] || category}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-8">
            {filteredSkills.length > 0 ? (
              <>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8">
                  {filteredSkills.map((skill) => {
                    const colorScheme = getYearColor(skill.years);

                    return (
                      <m.div
                        key={skill.name}
                        whileHover={{ scale: 1.05 }}
                        className={`flex flex-row items-center rounded-lg border px-2 py-1 ${
                          colorScheme.border
                        } ${colorScheme.bg} h-[50px] transition-all`}
                      >
                        <div className="mr-2 h-5 w-5 flex-shrink-0 text-xl">
                          {skill.icon || (
                            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700">
                              <span className="text-xs">{skill.name.charAt(0)}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex min-w-0 flex-1 flex-col justify-center">
                          <div className={`text-xs font-medium ${colorScheme.text} line-clamp-1`}>{skill.name}</div>
                          <div className={`text-[9px] ${colorScheme.text} opacity-80`}>{skill.years} ans</div>
                        </div>
                      </m.div>
                    );
                  })}
                </div>

                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  <div className="mb-2 w-full text-center">
                    <p className="text-xs text-gray-500 italic dark:text-gray-400">
                      Les couleurs indiquent les années d&#39;expérience professionnelle
                    </p>
                  </div>
                  {[10, 7, 4, 1].map((threshold) => {
                    const colorScheme = getYearColor(threshold);
                    const label =
                      threshold === 10
                        ? "10+ ans"
                        : threshold === 7
                          ? "7-9 ans"
                          : threshold === 4
                            ? "4-6 ans"
                            : "1-3 ans";

                    return (
                      <div
                        key={threshold}
                        className={`flex items-center rounded-full px-2 py-1 ${colorScheme.bg} ${colorScheme.border} border`}
                      >
                        <div className={`h-2 w-2 rounded-full ${colorScheme.badge} mr-1.5`}></div>
                        <span className={`text-xs ${colorScheme.text}`}>{label}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="mb-2 text-gray-400 dark:text-gray-600">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300">Aucune compétence trouvée</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Aucune compétence ne correspond à ce filtre.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="mb-2 flex items-center">
            <h3 className="text-sm font-medium">Compétences transversales</h3>
          </div>
          <Tabs defaultValue="methodologies" className="w-full">
            <TabsList className="mb-4 w-full">
              <TabsTrigger value="methodologies" className="flex-1 cursor-pointer">
                Méthodologies
              </TabsTrigger>
              <TabsTrigger value="softskills" className="flex-1 cursor-pointer">
                Soft Skills
              </TabsTrigger>
            </TabsList>

            <TabsContent value="methodologies" className="mt-0">
              <Card className="border-none p-0 shadow-none">
                <MagicCard gradientColor={"#4169e120"} className="h-full">
                  <CardHeader className="p-4 pb-2">
                    <CardTitle className="flex items-center text-lg font-semibold">
                      <span className="mr-2 h-5 w-1 rounded bg-gradient-to-r from-blue-500 to-indigo-500"></span>
                      Méthodologies
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="flex flex-wrap gap-2">
                      {methodologies.map((methodology, index) => (
                        <m.div
                          key={methodology.name}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                        >
                          <Badge className="flex items-center gap-1.5 border-blue-200 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 px-3 py-1.5 text-blue-800 hover:from-blue-500/20 hover:to-indigo-500/20 dark:border-blue-800 dark:text-blue-200">
                            <div className="flex h-4 w-4 items-center justify-center text-blue-800 dark:text-blue-200">
                              {methodology.icon || (
                                <div className="flex h-4 w-4 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                                  <span className="text-[10px] text-blue-800 dark:text-blue-200">
                                    {methodology.name.charAt(0)}
                                  </span>
                                </div>
                              )}
                            </div>
                            {methodology.name}
                          </Badge>
                        </m.div>
                      ))}
                    </div>
                  </CardContent>
                </MagicCard>
              </Card>
            </TabsContent>

            <TabsContent value="softskills" className="mt-0">
              <Card className="border-none p-0 shadow-none">
                <MagicCard gradientColor={"#00d49220"} className="h-full">
                  <CardHeader className="p-4 pb-2">
                    <CardTitle className="flex items-center text-lg font-semibold">
                      <span className="mr-2 h-5 w-1 rounded bg-gradient-to-r from-emerald-500 to-teal-500"></span>
                      Soft Skills
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="flex flex-wrap gap-2">
                      {softSkills.map((skill, index) => (
                        <m.div
                          key={skill.name}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                        >
                          <Badge className="flex items-center gap-1.5 border-emerald-200 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 px-3 py-1.5 text-emerald-800 hover:from-emerald-500/20 hover:to-teal-500/20 dark:border-emerald-800 dark:text-emerald-200">
                            <div className="flex h-4 w-4 items-center justify-center text-emerald-800 dark:text-emerald-200">
                              {skill.icon || (
                                <div className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900">
                                  <span className="text-[10px] text-emerald-800 dark:text-emerald-200">
                                    {skill.name.charAt(0)}
                                  </span>
                                </div>
                              )}
                            </div>
                            {skill.name}
                          </Badge>
                        </m.div>
                      ))}
                    </div>
                  </CardContent>
                </MagicCard>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Section>
  );
};
